export const DEFAULT_BROWSER_NOTIFICATIONS = {
  incomingCalls: true,
  missedCalls: true,
  voicemail: true,
  chat: true,
  // Patient text routing (INBOX_TEXT_ROUTING_FEATURE): new patient texts routed to you.
  // Only offered / delivered while routing is on — see text-routing-alerts.hook.ts.
  texts: true
};

export type BrowserNotificationType =
  keyof typeof DEFAULT_BROWSER_NOTIFICATIONS;
export type BrowserNotificationPreferences =
  typeof DEFAULT_BROWSER_NOTIFICATIONS;
export type BrowserPermission =
  | NotificationPermission
  | "insecure"
  | "unsupported"
  | "unavailable";

export function getBrowserPermission(): BrowserPermission {
  // Feature detection, not browser-name detection. Even desktop Chrome
  // cannot request notifications from an ordinary HTTP origin.
  if (!window.isSecureContext) return "insecure";
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

export const preferencesKey = (scope: string) =>
  `suno:browser-notifications:v1:${scope}`;

export function readBrowserPreferences(
  scope: string
): BrowserNotificationPreferences {
  try {
    const stored = JSON.parse(
      localStorage.getItem(preferencesKey(scope)) || "{}"
    );
    return Object.fromEntries(
      Object.entries(DEFAULT_BROWSER_NOTIFICATIONS).map(([key, fallback]) => [
        key,
        typeof stored?.[key] === "boolean" ? stored[key] : fallback
      ])
    ) as BrowserNotificationPreferences;
  } catch {
    return { ...DEFAULT_BROWSER_NOTIFICATIONS };
  }
}

export const isAppBackgrounded = () => document.hidden || !document.hasFocus();

// Each tab evaluates document.hidden / hasFocus for itself, so with two Suno
// tabs open the unfocused one would show a banner for a message the user is
// already reading in the other. The focused tab publishes a short-lived marker
// (heartbeat well inside the TTL) that other tabs consult before delivering.
export const FOREGROUND_KEY = "suno:browser-notifications:v1:foreground";
export const FOREGROUND_HEARTBEAT_MS = 5_000;
export const FOREGROUND_TTL_MS = 15_000;
const tabToken = Math.random().toString(36).slice(2);

export function startForegroundHeartbeat(): () => void {
  let timer: ReturnType<typeof setInterval> | undefined;
  const write = () => {
    try {
      localStorage.setItem(FOREGROUND_KEY, `${tabToken}:${Date.now()}`);
    } catch {
      /* Storage can be disabled; the per-tab focus check still applies. */
    }
  };
  // Only this tab's own marker is removed, never one another tab just wrote.
  const clear = () => {
    try {
      if (localStorage.getItem(FOREGROUND_KEY)?.startsWith(`${tabToken}:`))
        localStorage.removeItem(FOREGROUND_KEY);
    } catch {
      /* best effort */
    }
  };
  const update = () => {
    if (isAppBackgrounded()) {
      clearInterval(timer);
      timer = undefined;
      clear();
      return;
    }
    write();
    // The tick re-checks focus, so a missed blur event cannot pin the marker.
    if (!timer) timer = setInterval(update, FOREGROUND_HEARTBEAT_MS);
  };
  update();
  window.addEventListener("focus", update);
  window.addEventListener("blur", update);
  document.addEventListener("visibilitychange", update);
  window.addEventListener("pagehide", clear);
  return () => {
    clearInterval(timer);
    window.removeEventListener("focus", update);
    window.removeEventListener("blur", update);
    document.removeEventListener("visibilitychange", update);
    window.removeEventListener("pagehide", clear);
    clear();
  };
}

// The conversation open in a tab, shared so that whichever tab delivers a chat
// alert can tell the person is already reading it in the tab they are looking at.
export const OPEN_CONVERSATION_KEY =
  "suno:browser-notifications:v1:open-conversation";

export function publishOpenConversation(id: string | null) {
  try {
    if (id) {
      localStorage.setItem(OPEN_CONVERSATION_KEY, `${tabToken}|${id}`);
    } else if (
      localStorage.getItem(OPEN_CONVERSATION_KEY)?.startsWith(`${tabToken}|`)
    ) {
      localStorage.removeItem(OPEN_CONVERSATION_KEY);
    }
  } catch {
    /* Storage can be disabled; the open tab still checks itself. */
  }
}

// True when the Suno tab in front (this one, or another tab's fresh foreground
// marker) has this conversation open.
export function isOpenInFocusedSunoTab(id: string): boolean {
  try {
    const open = localStorage.getItem(OPEN_CONVERSATION_KEY);
    if (!open) return false;
    const separator = open.indexOf("|");
    const owner = open.slice(0, separator);
    if (open.slice(separator + 1) !== id) return false;
    if (owner === tabToken) return !isAppBackgrounded();
    const marker = localStorage.getItem(FOREGROUND_KEY);
    if (!marker) return false;
    const split = marker.lastIndexOf(":");
    const stamp = Number(marker.slice(split + 1));
    return (
      marker.slice(0, split) === owner &&
      Number.isFinite(stamp) &&
      Date.now() - stamp < FOREGROUND_TTL_MS
    );
  } catch {
    return false;
  }
}

// Another tab's fresh marker. This tab's own focus is checked directly.
export function hasForegroundSunoTab(): boolean {
  try {
    const marker = localStorage.getItem(FOREGROUND_KEY);
    if (!marker || marker.startsWith(`${tabToken}:`)) return false;
    const stamp = Number(marker.slice(marker.lastIndexOf(":") + 1));
    return Number.isFinite(stamp) && Date.now() - stamp < FOREGROUND_TTL_MS;
  } catch {
    return false;
  }
}

export interface BrowserAlert {
  type: BrowserNotificationType;
  id: string;
  title: string;
  body: string;
  onClick: () => void;
  requireInteraction?: boolean;
  silent?: boolean;
  icon?: string;
  // Alerts sharing a collapse key replace each other on screen (one banner
  // per conversation) while the ledger still deduplicates per id.
  collapseKey?: string;
  autoCloseMs?: number;
  // Chat alerts show even while Suno is the window in front (SPA-6711); every
  // other type defers to the in-app UI when someone is already looking at Suno.
  showInForeground?: boolean;
}

// One auto-close timer per on-screen banner (keyed by its tag). A newer alert
// with the same tag replaces the banner, so its timer must restart and the old
// one must never close the replacement. The replacement may come from another
// tab, so the latest alert per tag is also recorded in storage and an old timer
// stands down when it is no longer the latest.
const autoCloseTimers = new Map<string, ReturnType<typeof setTimeout>>();
const LATEST_ALERT_LIMIT = 100;

const latestAlertKey = (scope: string) =>
  `${preferencesKey(scope)}:latest-by-tag`;

function readLatestAlerts(scope: string): Record<string, string> {
  try {
    const stored = JSON.parse(
      localStorage.getItem(latestAlertKey(scope)) || "{}"
    );
    return stored && typeof stored === "object" ? stored : {};
  } catch {
    return {};
  }
}

function recordLatestAlert(scope: string, tag: string, alertId: string) {
  try {
    const latest = readLatestAlerts(scope);
    delete latest[tag];
    latest[tag] = alertId;
    const entries = Object.entries(latest).slice(-LATEST_ALERT_LIMIT);
    localStorage.setItem(
      latestAlertKey(scope),
      JSON.stringify(Object.fromEntries(entries))
    );
  } catch {
    /* best effort: the in-tab timer map still covers this tab */
  }
}

function scheduleAutoClose(
  scope: string,
  tag: string,
  alertId: string,
  notification: Notification,
  autoCloseMs: number | undefined
) {
  clearTimeout(autoCloseTimers.get(tag));
  autoCloseTimers.delete(tag);
  recordLatestAlert(scope, tag, alertId);
  if (!autoCloseMs) return;
  const timer = setTimeout(() => {
    if (autoCloseTimers.get(tag) !== timer) return;
    autoCloseTimers.delete(tag);
    const latest = readLatestAlerts(scope)[tag];
    if (latest !== undefined && latest !== alertId) return;
    notification.close();
  }, autoCloseMs);
  autoCloseTimers.set(tag, timer);
  // A banner closed early (clicked, dismissed, replaced) takes its timer with it.
  notification.addEventListener?.("close", () => {
    if (autoCloseTimers.get(tag) !== timer) return;
    clearTimeout(timer);
    autoCloseTimers.delete(tag);
  });
}

// A short lock serializes competing tabs. The bounded ledger contains event
// IDs only, never caller names or message previews. A stable tag is also used
// on browsers without Web Locks. Failures must never interrupt call/chat delivery.
export async function deliverBrowserAlert(
  scope: string,
  alert: BrowserAlert,
  isAllowed: () => boolean,
  onCreated: (notification: Notification) => void
): Promise<void> {
  const deliver = () => {
    if (
      !isAllowed() ||
      getBrowserPermission() !== "granted" ||
      (!alert.showInForeground && !isAppBackgrounded())
    )
      return;
    // Incoming calls ring only in the tab that owns the softphone, and its
    // banner is the one-click answer path, so it shows even when another Suno
    // tab is focused. Everything else defers to the focused tab's in-app UI,
    // except alerts that ask to show in the foreground too.
    if (
      alert.type !== "incomingCalls" &&
      !alert.showInForeground &&
      hasForegroundSunoTab()
    )
      return;
    const key = `${preferencesKey(scope)}:delivered`;
    const id = `${alert.type}:${alert.id}`;
    let ledger: string[] = [];
    try {
      const stored = JSON.parse(localStorage.getItem(key) || "[]");
      if (Array.isArray(stored))
        ledger = stored.filter((entry) => typeof entry === "string");
    } catch {
      /* Storage can be disabled. Native tags still replace duplicates. */
    }
    if (ledger.includes(id)) return;
    try {
      // renotify re-alerts when a collapsed banner is replaced; it is not in
      // TypeScript's NotificationOptions yet.
      const tag = `${scope}:${alert.type}:${alert.collapseKey ?? alert.id}`;
      const options: NotificationOptions & { renotify?: boolean } = {
        body: alert.body,
        tag,
        renotify: alert.collapseKey !== undefined,
        icon: alert.icon,
        requireInteraction: alert.requireInteraction,
        silent: alert.silent
      };
      const notification = new Notification(alert.title, options);
      notification.onclick = (event) => {
        notification.close();
        if (!isAllowed()) {
          event.preventDefault();
          return;
        }
        // Preserve the browser's native focus of the notification's owning
        // tab, as well as requesting focus before running its click action.
        window.focus();
        alert.onClick();
      };
      scheduleAutoClose(scope, tag, id, notification, alert.autoCloseMs);
      onCreated(notification);
      try {
        localStorage.setItem(key, JSON.stringify([...ledger, id].slice(-250)));
      } catch {
        /* best effort */
      }
    } catch {
      /* Unsupported constructors / permission changes retain in-app alerts. */
    }
  };
  try {
    if (navigator.locks) {
      await navigator.locks.request(
        `${preferencesKey(scope)}:delivery`,
        deliver
      );
    } else {
      deliver();
    }
  } catch {
    /* A failed lock must not break the real-time event handler. */
  }
}

const TEST_NOTIFICATION_TTL_MS = 10_000;

// Bypasses the background, preference and ledger gates so the user can confirm
// end-to-end OS delivery from the settings page while looking at it.
// requireInteraction exercises the same path as incoming calls: on macOS,
// Chrome shows those through a separate "Google Chrome Helper (Alerts)" entry.
export function showTestBrowserNotification(): boolean {
  if (getBrowserPermission() !== "granted") return false;
  try {
    const notification = new Notification(
      "Suno desktop notifications are working",
      {
        body: "Call and chat alerts will look like this.",
        tag: "suno:browser-notifications:test",
        requireInteraction: true
      }
    );
    notification.onclick = () => {
      notification.close();
      window.focus();
    };
    setTimeout(() => notification.close(), TEST_NOTIFICATION_TTL_MS);
    return true;
  } catch {
    return false;
  }
}

export function getDesktopPlatform(): "mac" | "windows" | "other" {
  const platform = (
    (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform ||
    navigator.platform ||
    ""
  ).toLowerCase();
  if (platform.startsWith("mac")) return "mac";
  if (platform.startsWith("win")) return "windows";
  return "other";
}
